import { Navigate, useLocation, useParams } from 'react-router';
import { InlineError } from '../../ui/InlineError';
import { Skeleton } from '../../ui/Skeleton';
import { PageHeader } from '../shell/PageHeader';
import { keyDestroyed, useKey } from '../../api/queries';
import { isRyptError } from '../../api/errors';
import { finalMessageFor } from '../../api/error-table';
import { shortId } from '../../lib/format';
import { containers as copy } from '../../copy/containers';
import { keyDetail } from '../../copy/keyDetail';
import { containerPath, keyPath, type ContainerSection } from './paths';
import { useCurrentContainer } from './useCurrentContainer';
import { RetryButton } from '../shell/RetryButton';

/**
 * Shown while a redirect waits on what it needs to know, or why it cannot go on: which
 * read failed, as the pages say it, and Retry. The error has a heading over it, so the
 * page it stops on still has an h1.
 */
function Waiting({ error, failed, onRetry }: { error: unknown; failed?: string; onRetry?: () => void }) {
  if (!error) return <Skeleton variant="block" lines={3} label={copy.finding} />;
  const e = isRyptError(error) ? error : null;
  return (
    <>
      <PageHeader title={copy.finding} />
      <InlineError
        {...(failed ? { title: failed } : {})}
        {...(e ? { requestId: e.requestId } : {})}
        {...(onRetry
          ? {
              action: (
                <RetryButton onRetry={onRetry} />
              ),
            }
          : {})}
      >
        {e ? finalMessageFor(e) : String(error)}
      </InlineError>
    </>
  );
}

/**
 * A redirect, with the skeleton still drawn beside it: <Navigate> renders nothing, and the
 * page it goes to loads its code first, so on its own it would leave main blank for that
 * long. A function, not a component, so the skeleton is the same one the wait showed and
 * is not mounted again.
 */
function redirect(to: string) {
  return (
    <>
      <Waiting error={null} />
      <Navigate to={to} replace />
    </>
  );
}

/**
 * `/overview`, `/keys`, `/api-tokens`, `/firewalls` and `/usage`: the same page of the
 * current container. These are what sign-in's `returnTo`, the landing page and every
 * bookmark from before containers point at, so they keep working; the query string comes
 * along, which carries the keys list's filters and billing's checkout return. The API
 * tokens page was `api-keys` before the rename, flat and under a container, and both of
 * those land here too: under a container, the current one is the URL's.
 */
export function LegacySection({ section }: { section: ContainerSection }) {
  const { search } = useLocation();
  const { current, containers } = useCurrentContainer();
  if (!current) {
    return <Waiting error={containers.error} failed={copy.loadFailed} onRetry={() => void containers.refetch()} />;
  }
  return redirect(`${containerPath(current.id, section)}${search}`);
}

/**
 * `/keys/:keyId/*`: the key's page in its own container. A destroyed key 404s and so
 * names no container; its audit log is still readable, from any container's page,
 * because the console reads audit tenant-wide, so it goes to the current one's.
 */
export function LegacyKey() {
  const { keyId = '', '*': rest = '' } = useParams();
  const { search } = useLocation();
  const key = useKey(keyId);
  const { current, containers } = useCurrentContainer();
  const tab = rest === 'versions' || rest === 'activity' ? rest : undefined;

  if (key.data) return redirect(`${keyPath(key.data.container_id, keyId, tab)}${search}`);
  if (keyDestroyed(key.error)) {
    if (!current) {
      return <Waiting error={containers.error} failed={copy.loadFailed} onRetry={() => void containers.refetch()} />;
    }
    return redirect(keyPath(current.id, keyId, 'activity'));
  }
  return (
    <Waiting error={key.error} failed={keyDetail.loadFailed(shortId(keyId))} onRetry={() => void key.refetch()} />
  );
}
